import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Groupper, Header, Icon, Input, Message, Table, Toolbar, Modal, useFrontier } from "react-frontier2";
import { SetLoading } from "@arema/components/Classes";
import { bindClick, bindFormChange } from "@arema/components/Util";
import { DeletedBanner, ErrorHeader, NotFound, UserLog } from "../../components";
import { useUser } from "../../AdminHooks";
import { useTitle } from "@arema/components/Hooks";
import { BankBin } from "../../AdminClasses";
import API from "../../API";
import Validator from "@arema/components/Validator";
import UserAccess from "../../UserAccess";
import moment from "moment";

enum Modals {
	ADD_BIN_NUMBER = 1,
	DELETE_BIN_NUMBER = 2,
	CLEAR_BIN_NUMBERS = 3,
	DELETE_BANK = 4,
}

var BankView = ()=>{
	var params = useParams();
	var { user, hasAccess } = useUser();
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [bank, setBank] = useState<BankBin>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [binNumber, setBinNumber] = useState<string>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [selectedBin, setSelectedBin] = useState<BankBin["bins"][0]>(null);
	var bank_id = params?.bank_id && !Number.isNaN(parseInt(params.bank_id)) ? parseInt(params.bank_id) : null;

	useEffect(()=>{
		if(bank_id && !bank){
			loadData(bank_id);
		}
	}, [bank_id]);

	var loadData = (bank_id: number)=>{
		API.getBank(bank_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setTitle(`Banco ${res.data.bank_name}`);
			setBank(res.data)
		}).catch(err=>{
			setLoadError('Hubo un error cargando el banco (LCL-BNKVIW-1)');
		});
	}

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(bank, {
			bank_name: [{
				rule: 'length', params: [3, 64], label: 'Nombre'
			}],
			description: [{
				rule: 'length', params: [3], label: 'Descripcion', skipEmpty: true
			}],
		});
		setErrorPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.editBankBin(bank.bank_id, bank.bank_name, bank.description).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			Toast.success('Se ha editando el banco');
		}).catch(err=>{
			setErrorPrompts(['Hubo un error inesperado editando el banco (LCL-BNKVIW-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var saveBins = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.CATALOGS.BANK_BIN_EDIT)) return;
		var input_bin_list = binNumber.split(/\r?\n|\r|\n/g).filter(s=>s !== '').map(s=>s.trim());
		if(input_bin_list.length === 0){
			return setModalPrompts(['Los BINs ingresados no son válidos']);
		}

		var bin_list : string[] = [];
		for(var bin of input_bin_list){
			if(!(/[0-9]{6,8}/).test(bin)) continue;
			if(bank.bins.findIndex(b=>b.bin === bin)==-1){
				bin_list.push(bin);
			}
		}

		if(bin_list.length==0){
			return setModalPrompts(['Los BINs ingresados no son válidos'])
		}

		setLoading(true);
		API.saveBinNumbers(bank.bank_id, bin_list).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se han guardado los BINs correctamente');
			setShownModal(null);
			setBank(b=>{
				var newbins = res.data
				b.bins.push(...newbins) 
				return b;
			});
			setBinNumber('');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado guardando los BINs (LCL-BNKVIW-3)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var deleteBin = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteBinNumber(selectedBin.number_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha eliminado correctamente');
			setShownModal(null);
			setSelectedBin(null);
			setBank(b=>{
				b.bins = b.bins.filter(b=>b.number_id !== selectedBin.number_id);
				return b;
			})
		}).catch(err=>{
			setModalPrompts(['Error inesperado editando los BINs (LCL-BNKVIW-4)']);
		}).finally(()=>{
			setLoading(false);
		});

	}

	var clearBins = (setLoading: SetLoading)=>{
		setLoading(true);
		setModalPrompts(null);
		API.clearBinNumbers(bank.bank_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se han eliminado los BINs correctamente');
			setShownModal(null);
			setBank(b=>{
				b.bins = [];
				return b;
			})
		}).catch(err=>{
			setModalPrompts(['Error inesperado editando los BINs (LCL-BNKVIW-5)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onClickDeleteBin = (bin: BankBin["bins"][0])=>{
		return ()=>{
			setSelectedBin(bin)
			setShownModal(Modals.DELETE_BIN_NUMBER)
		}
	}

	var deleteBank = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.CATALOGS.BANK_DELETE)) return;
		setLoading(true);
		setModalPrompts(null);
		API.deleteBank(bank.bank_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha eliminado el banco');
			setBank({
				...bank,
				deleted: true,
				user_deleted: user.user_id,
				user_deleted_name: user.name,
				date_deleted: moment().unix(),
			});
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el banco (LCL-BNKVIW-6']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	if(!bank_id){
		return <NotFound />
	}
	if(loadError){
		return <ErrorHeader error={loadError} />
	}
	if(!bank){
		return <Header text="Cargando banco" loading />
	}

	var onChangeField = bindFormChange(bank, setBank);

	return <>
		{!!bank?.deleted && <DeletedBanner date_deleted={bank.date_deleted} />}
		{bank &&
			<Groupper title={`Editar Banco`} style={{ maxWidth: 500, margin: 'auto' }} titleRight={`ID: ${bank_id}`} actions={!bank.deleted && (
				<Button text="Guardar" color="black" onClick={submit} />
			)}>
				<Input label="Nombre" placeholder="Nombre del Banco" value={bank.bank_name} onChange={onChangeField('bank_name')} />
				<Input label="Descripcion" placeholder="Descripcion" value={bank.description} onChange={onChangeField('description')} />
				<Message list={errorPrompts} type="error" style={{ marginTop: 15 }} />
			</Groupper>
		}

		<Groupper title='BINs del banco' style={{ maxWidth: 500, margin: '15px auto' }}>
			<Toolbar fitted style={{ borderBottom: 0 }}>
				{!bank.deleted && hasAccess(UserAccess.CATALOGS.BANK_BIN_EDIT) && <>
					<Toolbar.Item text='Agregar BINs' iconName='plus-circle' onClick={bindClick(setShownModal, Modals.ADD_BIN_NUMBER)} />
					<Toolbar.Item text='Vaciar' iconName='trash' onClick={bindClick(setShownModal, Modals.CLEAR_BIN_NUMBERS)} />
				</>}
			</Toolbar>
			<Table fitted top last>
				<Table.Head
					collapsingIndexes={[0, 2]}
					centeredIndexes={[2]}
					data={[['ID', 'BIN', <Icon name="remove" />]]}
				/>
				<Table.Body>
					{bank.bins?.length===0 && (
						<Table.Cell empty row colSpan={100} value={'El banco no tiene BINs agregados'} />
					)}
					{bank?.bins?.map(a=>(
						<Table.Row data={[
							a.number_id,
							a.bin,
							<Button iconName='remove' color='black' size='tiny' style={{ minWidth: 40 }} onClick={onClickDeleteBin(a)} />
						]} />
					))}
				</Table.Body>
			</Table>
		</Groupper>
		<UserLog user={bank} />
		{!bank.deleted && hasAccess(UserAccess.CATALOGS.BANK_DELETE) && (
			<Button text="Eliminar banco" color="red" basic style={{ display: 'block', margin: 'auto', width: 150, marginTop: 15 }} onClick={bindClick(setShownModal, Modals.DELETE_BANK)} />
		)}

		<Modal open={shownModal === Modals.ADD_BIN_NUMBER} size='tiny' onClose={bindClick(setShownModal)}>
			<Modal.Header>Agregar BINs</Modal.Header>
			<Modal.Content>
				<Input textarea textareaRows={10} label="Lista de BINs" placeholder="Lista de BINs, separados por linea" value={binNumber} onChange={setBinNumber} />
				<Message type="info">
					Ingresar una lista de BINs, en caso de ser multiples, se deberán de agregar separados uno por línea.
					<ul className="list">
						<li>Un BIN es un número de entre 6-8 digitos</li>
						<li>En caso de haber repetidos, se ignorarán</li>
						<li>Favor de asegurarse de que el bin ingresado sea del banco <b>{bank.bank_name}</b></li>
					</ul>
				</Message>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color="black" onClick={saveBins} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal === Modals.DELETE_BIN_NUMBER} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Eliminar BIN</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Eliminar BIN?' subtext={`Eliminando ${selectedBin?.bin}`} />
				<Message type="error" centered>
					Una vez eliminado, no podrá ser recuperado
				</Message>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Eliminar' color={'red'} onClick={deleteBin} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal === Modals.CLEAR_BIN_NUMBERS} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Eliminar Todos los BINs</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Eliminar todos los BINs?' />
				<Message type="error" centered>
					Una vez eliminados, <b>no</b> podrán ser recuperados
				</Message>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Eliminar' color={'red'} onClick={clearBins} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.DELETE_BANK} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Eliminar banco</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Eliminar banco?' subtext={'Los eventos con exclusividad y cualquier otra referencia a este banco será eliminado'} />
				<Message type="error" centered>
					Una vez eliminado, <b>no</b> podrá ser recuperado
				</Message>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Eliminar' color={'red'} onClick={deleteBank} />
			</Modal.Actions>
		</Modal>
	</>
}

export default BankView;